import React, { useContext, useMemo, useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import FeedPage from '../pages/FeedPage';
import WalletPage from '../pages/WalletPage';
import Zwallet from '../pages/WalletPage'; // Or wherever your Zwallet component is
import IdentityDashboard from '../components/ensapp/IdentityDashboard';
import AvatarDisplay from './AvatarDisplay';
import ZearchPage from '../pages/ZearchPage';
import HelpCenterEmbedded from '../components/embeddable/HelpCenterEmbedded';
import MobileHamburgerMenu from '../components/MobileHamburgerMenu';
import ProfilePage from '../pages/ProfilePage';
import NoticesPage from '../pages/NoticesPage';
import PostPage from '../pages/PostPage';
import Privacy from '../pages/Privacy';
import ENSSearchPage from '../pages/ENSSearchPage';

// ZhatWalletCommunicator component (unchanged)
const ZhatWalletCommunicator = ({ mode, redirectUrl }) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [countdown, setCountdown] = useState(5);
  const location = useLocation();

  const sendToZhat = useMemo(() => (type, address) => {
    console.log(`Sending message to zhat-app: ${type} for ${address}`);

    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        source: 'zwallet',
        type: type,
        address: address,
        shouldCheckENS: true,
        timestamp: Date.now()
      }, '*');
      console.log('Message sent via window.opener');
    }

    const message = {
      source: 'zwallet',
      type: type,
      address: address,
      shouldCheckENS: true,
      timestamp: Date.now(),
      for: 'zhat'
    };

    try {
      localStorage.setItem('zwallet_to_zhat', JSON.stringify(message));
      console.log('Message stored in localStorage');

      window.dispatchEvent(new StorageEvent('storage', {
        key: 'zwallet_to_zhat',
        newValue: JSON.stringify(message)
      }));
    } catch (e) {
      console.warn('Failed to set localStorage:', e);
    }

    if (redirectUrl && type === 'ZWALLET_CONNECTED') {
      console.log('Found redirect URL:', redirectUrl);
    }

    return message;
  }, [redirectUrl]);

  const TempNotifications = () => <div style={{color:'white'}}>Inline Notifications</div>;

  const handleZhatConnected = useMemo(() => (data) => {
    console.log('Wallet connected from standalone mode:', data);
    sendToZhat('ZWALLET_CONNECTED', data.address);
    setSuccessMessage('Wallet Connected!');
    setShowSuccess(true);
  }, [sendToZhat]);

  const handleZhatCreated = useMemo(() => (address) => {
    console.log('Wallet created from standalone mode:', address);
    sendToZhat('ZWALLET_CREATED', address);
    setSuccessMessage('Wallet Created!');
    setShowSuccess(true);
  }, [sendToZhat]);

  useEffect(() => {
    if (!showSuccess) return;

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);

          if (redirectUrl) {
            window.location.href = redirectUrl;
          } else if (window.opener && !window.opener.closed) {
            window.close();
          }

          setShowSuccess(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showSuccess, redirectUrl]);

  const closeSuccess = () => {
    setShowSuccess(false);
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else if (window.opener && !window.opener.closed) {
      window.close();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--black)',
      color: 'var(--text-light)',
      padding: 0,
      position: 'relative'
    }}>
      {showSuccess && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: 'var(--gold)',
          color: 'var(--black)',
          padding: '15px 20px',
          borderRadius: '8px',
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <span>{successMessage}</span>
          <button
            onClick={closeSuccess}
            style={{
              background: 'var(--black)',
              color: 'var(--gold)',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '12px'
            }}
          >
            Return Now
          </button>
          <span style={{ fontWeight: 'bold', minWidth: '20px', textAlign: 'center' }}>
            {countdown}
          </span>
        </div>
      )}

      <WalletPage
        standalone={true}
        initialMode={mode || 'connect'}
        skipConnectionScreen={false}
        onConnected={handleZhatConnected}
        onWalletCreated={handleZhatCreated}
      />

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

// Placeholder component for Explore (you can replace later)
const ExplorePage = () => (
  <div className="page-container">
    <div className="page-header">
      <h2 style={{ color: 'var(--gold)' }}>Explore</h2>
      <p style={{ color: 'var(--text-secondary)' }}>Discover trending topics and conversations</p>
    </div>
    <div className="card" style={{ marginTop: '1.5rem' }}>
      <h3 style={{ color: 'var(--gold)' }}>Coming Soon</h3>
      <p style={{ color: 'var(--text-secondary)' }}>
        Explore functionality will be available in the next update.
      </p>
    </div>
  </div>
);

const NotFoundPage = () => {
  const navigate = useNavigate();

  return (
    <div className="page-container">
      <div className="page-header">
        <h2 style={{ color: 'var(--gold)' }}>Page Not Found</h2>
        <p style={{ color: 'var(--text-secondary)' }}>This page doesn't exist in Zpost</p>
      </div>
      <div className="card" style={{ marginTop: '1.5rem', textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '3rem', color: 'var(--gold)', marginBottom: '1rem' }}>
          <i className="fas fa-compass"></i>
        </div>
        <h3 style={{ color: 'var(--gold)' }}>Lost in the Feed?</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
          The page you're looking for doesn't exist or hasn't been built yet.
        </p>
        <button
          onClick={() => navigate('/home')}
          className="btn btn-gold"
          style={{
            padding: '0.75rem 1.5rem',
            borderRadius: '9999px'
          }}
        >
          Return to Home Feed
        </button>
      </div>
    </div>
  );
};

const PrivateApp = () => {
  const { isAuthenticated, ensName, address, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isForZhat = searchParams.get('for') === 'zhat';
  const isWalletRoute = location.pathname.startsWith('/wallet');
  const mode = searchParams.get('mode');
  const redirectUrl = searchParams.get('redirect');

  if (isWalletRoute && isForZhat) {
    console.log('PrivateApp: Showing standalone wallet for zhat-app, mode:', mode);
    return <ZhatWalletCommunicator mode={mode} redirectUrl={redirectUrl} />;
  }

  return (
    <div className="zpost-private-app">
      <MobileHamburgerMenu ensName={ensName} />

      <div className="zpost-main-layout">
        <aside className="zpost-left-sidebar">
          <div className="sidebar-sticky">
            <div className="sidebar-logo">
              <button
                onClick={() => navigate('/home')}
                className="logo-button"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start'
                }}
              >
                <span className="gold-logo" style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--gold)' }}>
                  Zpost
                </span>
                <span className="subtitle" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                  Your voice, your rules
                </span>
              </button>
            </div>
            <LeftSidebar />
          </div>
        </aside>

        <main className="zpost-main-content-area">
          <div className="feed-header-sticky">
            <div className="feed-header-content">
              <h2 className="feed-title" style={{ color: 'var(--text-light)', margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>
                Home
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                <i className="fas fa-globe-americas" style={{ color: 'var(--gold)' }}></i>
                <span>For you</span>
                <span style={{ margin: '0 0.5rem' }}>•</span>
                <i className="fas fa-users" style={{ color: 'var(--text-secondary)' }}></i>
                <span>Following</span>
              </div>
            </div>
          </div>

            <div className="zpost-feed-container">
              <Routes>
                {/* ✅ Make /home actually render the feed */}
                <Route path="/home" element={<FeedPage />} />

                {/* ✅ Aliases to match LeftSidebar links */}
                <Route path="/home/wallet" element={<WalletPage />} />
                <Route
                  path="/home/identity"
                  element={
                    <IdentityDashboard
                      integrationEnabled={true}
                      receivedAddress={address}
                      identityAuth={{
                        walletAddress: address,
                        ensName: ensName,
                        isAuthenticated: isAuthenticated
                      }}
                      logoutIdentity={logout}
                      onDomainMinted={(newDomain) => {
                        console.log('New domain minted:', newDomain);
                      }}
                      sendTransactionToWallet={null}
                      awaitWalletResponse={null}
                      walletPopupRef={null}
                      walletIsReady={isAuthenticated}
                      zpostProvider={null}
                      ensureSessionToken={null}
                    />
                  }
                />

                {/* Existing routes */}
                <Route path="/zearch" element={<ZearchPage />} />
                <Route path="/wallet" element={<WalletPage />} />
                <Route path="/help" element={<HelpCenterEmbedded />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/ens-search" element={<ENSSearchPage />} />

                {/* ✅ Notices route */}
                <Route path="/notices" element={<NoticesPage />} />

                {/* ✅ Individual post page (NEW) */}
                <Route path="/post/:id" element={<PostPage />} />

                <Route
                  path="/identity"
                  element={
                    <IdentityDashboard
                      integrationEnabled={true}
                      receivedAddress={address}
                      identityAuth={{
                        walletAddress: address,
                        ensName: ensName,
                        isAuthenticated: isAuthenticated
                      }}
                      logoutIdentity={logout}
                      onDomainMinted={(newDomain) => {
                        console.log('New domain minted:', newDomain);
                      }}
                      sendTransactionToWallet={null}
                      awaitWalletResponse={null}
                      walletPopupRef={null}
                      walletIsReady={isAuthenticated}
                      zpostProvider={null}
                      ensureSessionToken={null}
                    />
                  }
                />

                <Route path="/ens" element={<Navigate to="/home/identity" replace />} />
                <Route path="/explore" element={<ExplorePage />} />

                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/profile/:address" element={<ProfilePage />} />

                {/* Catch-all */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </div>
        </main>

        <aside className="zpost-right-sidebar">
          <div className="sidebar-sticky">
            <RightSidebar />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default PrivateApp;
